import React, { useEffect, useMemo, useState } from "react";
import { Dialog, Button, Spinner, Badge } from "@/components/ui";
import { SmartLink, SmartLinkReportType, SmartLinkStats } from "@/services/bedrock/dtos";
import { SmartLinkServices } from "@/services/bedrock";
import { BarChart3, FileDown } from "lucide-react";
import { kFormatter } from "@/utils";
import { exportToXlsx, getAutoWidths } from "@/utils/xlsxExportHelpers";
import { format, startOfMonth } from "date-fns";
import { DateRange } from "react-day-picker";
import { EditableCampaign, Prompt, XlsxSheet } from "@/interfaces";
import { toast } from "sonner";
import { ReportTabs } from "@/containers/Coach/SmartLink/StatsDialog/components/ReportTabs";
import { ReportDateRange } from "@/components/ReportDateRange";
import { generateTabs } from "@/containers/Coach/SmartLink/StatsDialog/statsExportHelper";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "Prompt" | "Campaign";
  entity: Prompt | EditableCampaign;
  smartLink?: SmartLink;
}

export const SmartLinkAggregatedStatsDialog: React.FC<Props> = ({ open, onOpenChange, type, entity, smartLink }) => {
  const [stats, setStats] = useState<SmartLinkStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [preset, setPreset] = useState<string>("this_month");
  const [date, setDate] = useState<DateRange | undefined>({
    from: startOfMonth(new Date()),
    to: new Date(),
  });

  const entityName = "name" in entity ? entity.name : "title" in entity ? entity.title : "";

  const handleExport = async () => {
    if (!stats) return;

    setIsExporting(true);
    try {
      const dateRangeString =
        preset === "all_time"
          ? "Timeframe: All Time"
          : `Timeframe: ${format(date!.from!, "LLL dd, y")} - ${format(date?.to || new Date(), "LLL dd, y")}`;

      const preparedByString = `Prepared by SocialCoach on ${format(new Date(), "PPp")}`;
      const linkInfoString = `${type} Smart Link Stats: ${entityName}`;
      const totalClicksString = `Total Clicks: ${stats.totalClicks}`;

      const reportConfig = generateTabs(stats);

      const sheets: XlsxSheet[] = reportConfig.map(config => ({
        name: config.name.substring(0, 31),
        data: config.data,
        columns: getAutoWidths(config.data),
        headerRows: [
          [preparedByString],
          [linkInfoString],
          [dateRangeString],
          [totalClicksString],
          [`Report: ${config.name}`],
          [`Description: ${config.desc}`],
        ],
      }));

      const filename = `SmartLink_Stats_${entityName.replace(/\s+/g, "_")}_${format(new Date(), "yyyy-MM-dd")}`;
      await exportToXlsx(filename, sheets);
      toast.success("Excel report exported successfully!");
    } catch (error) {
      console.error("Export failed", error);
      toast.error("Failed to generate Excel report.");
    } finally {
      setIsExporting(false);
    }
  };

  const reports: SmartLinkReportType[] = useMemo(() => {
    let list: SmartLinkReportType[] = [
      "total_clicks",
      "clicks_by_day",
      "clicks_by_network",
      "clicks_by_region",
      "clicks_by_player",
    ];
    if (!("promptId" in entity)) {
      list.push("clicks_by_prompt");
    }
    if (!("campaignId" in entity)) {
      list.push("clicks_by_campaign");
    }
    if (!smartLink) {
      list.push("clicks_by_smart_link");
    }
    return list;
  }, [entity, smartLink]);

  useEffect(() => {
    if (open && entity) {
      if (preset !== "all_time" && !date?.from) return;

      setIsLoading(true);
      const start = date?.from ? format(date.from, "yyyy-MM-dd") : undefined;
      const end = date?.to ? format(date.to, "yyyy-MM-dd") : undefined;
      const request =
        "promptId" in entity
          ? SmartLinkServices.getSmartLinkStatsByPrompt(entity.promptId!, reports, start, end, smartLink?.id)
          : "campaignId" in entity
          ? SmartLinkServices.getSmartLinkStatsByCampaign(entity.campaignId!, reports, start, end)
          : undefined;

      if (!request) return;
      request
        .then(setStats)
        .catch(console.error)
        .finally(() => setIsLoading(false));
    }
  }, [open, entity, date, preset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="xl">
      <Dialog.Header>
        <div className="flex items-center justify-between w-full">
          <div className="flex flex-col gap-1">
            <Dialog.Title className="text-xl">Smart Link Analytics</Dialog.Title>
            <Dialog.Description className="flex items-center gap-2">
              <span className="font-semibold text-foreground">{entityName}</span>
            </Dialog.Description>
          </div>
          {stats && (
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-end">
                <Badge
                  variant="secondary"
                  className="text-lg font-bold px-3 py-1 bg-primary/5 text-primary border-primary/20"
                >
                  {kFormatter(stats.totalClicks)} Clicks
                </Badge>
              </div>
            </div>
          )}
        </div>
      </Dialog.Header>

      <Dialog.Content className="min-h-[500px] overflow-y-auto max-h-[calc(100vh-20rem)]">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <Spinner className="size-8" />
            <p className="text-muted-foreground animate-pulse">Generating report...</p>
          </div>
        ) : stats ? (
          <div className={"flex flex-col gap-6"}>
            <ReportDateRange date={date} preset={preset} onDateChange={setDate} onPresetChange={setPreset} />
            <ReportTabs stats={stats} />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-32 text-muted-foreground gap-4">
            <BarChart3 className="size-12 opacity-20" />
            <p>No statistical data available for this link yet.</p>
          </div>
        )}
      </Dialog.Content>

      <Dialog.Footer>
        <Button variant="outline" onClick={handleExport} disabled={isExporting} loading={isExporting}>
          <FileDown className="size-4" />
          Export
        </Button>
        <Button onClick={() => onOpenChange(false)}>Close Report</Button>
      </Dialog.Footer>
    </Dialog>
  );
};
